import { Bridge } from '@mc/mc-jsbridge'
import { NODE_ENV } from '@/defines/env'
import {
  appUrlIndex,
  nativeRoutes,
  miniProgramUrlIndex,
  h5UrlIndex
} from './routes'
import { miniProgramEnv, platform, appVersion } from '@/defines/environment'
import { isMallApp } from '@mc/mc-base-env/src/web/platform'
import Url from '@mc/mc-utils/js/url/url'
import { mpsCombination } from '../mps_combination'
import { h5Redirect } from '../h5_wrap/redirect'
import {
  getTracerCurrentPageInfo,
  getTracerExtraParams
} from '../../tracer/utils'
import { ANDROID_SESSION_ID } from '../const/version'
import { openApp } from '../../app_download'
/**
 * 根据传入的 app url 匹配到合适的 nativeRoute 对象
 * @param {string} appUrl - app url
 * @returns {nativeRoute} - 匹配到的原生路由对象
 */
const matchAppUrl = appUrl => {
  const urlWithoutQueries = appUrl.split('?')[0]
  const nativeRouteKey = appUrlIndex[urlWithoutQueries]
  return nativeRoutes[nativeRouteKey]
}

/**
 * 根据传入的小程序 url 匹配到合适的 nativeRoute 对象
 * @param {string} miniProgramUrl - mp url
 * @returns {nativeRoute} - 匹配到的原生路由对象
 */
const matchMiniProgramUrl = miniProgramUrl => {
  const urlWithoutQueries = miniProgramUrl.split('?')[0]
  const nativeRouteKey = miniProgramUrlIndex[urlWithoutQueries]
  return nativeRoutes[nativeRouteKey]
}

/**
 * 根据传入的 h5 url 匹配到合适的 nativeRoute 对象
 * @param {string} h5Url - h5 url
 * @returns {nativeRoute} - 匹配到的原生路由对象
 */
const matchH5Url = h5Url => {
  const [path, hash] = h5Url.split('#')
  const pathWithoutQueries = path.split('?')[0]
  const hashWithoutQueries = hash ? hash.split('?')[0] : ''
  const urlWithoutQueries =
    pathWithoutQueries[pathWithoutQueries.length - 1] === '/' &&
    hashWithoutQueries[0] === '/'
      ? `${pathWithoutQueries}${hashWithoutQueries.slice(1)}`
      : `${pathWithoutQueries}${hashWithoutQueries}`
  // 因为 h5 的 url, 有些是动态匹配，所以不能用相等判断了，暂且用 indexOf 检查，其实也有缺陷。
  const matchedH5Url = Object.keys(h5UrlIndex).find(
    url => urlWithoutQueries.indexOf(url) > -1
  )
  if (matchedH5Url) {
    const nativeRouteKey = h5UrlIndex[matchedH5Url]
    return nativeRoutes[nativeRouteKey]
  } else {
    return undefined
  }
}

/**
 * 跳转到小程序原生页面
 * @param {nativeRoute} nativeRoute - 要跳转到的原生路由对象
 * @param {Object.<string, string>} queries - queries 参数
 */
const redirectToMiniProgram = (nativeRoute, queries, ssuIdFromH5) => {
  // 兼容h5 给小程序发消息不及时的问题
  const tracerCurrentPageInfo = getTracerCurrentPageInfo()
  const tracerExtraParams = getTracerExtraParams()

  if (nativeRoute?.mp) {
    if (nativeRoute.mp === nativeRoutes.goodsDetail.mp) {
      ssuIdFromH5 && (queries.id = ssuIdFromH5)
    }
    const queriesStringObj = Object.assign(
      {},
      tracerCurrentPageInfo,
      tracerExtraParams,
      queries
    )

    const queriesString = new Url('').stringifyQueryObj(queriesStringObj)
    if (nativeRoute.isTab) {
      if (nativeRoute.mp === nativeRoutes.goodsList.mp) {
        mpsCombination.postMessage({
          data: {
            type: 'market',
            data: queries
          }
        })
      }
      mpsCombination.switchTab({
        url: nativeRoute.mp
      })
    } else {
      mpsCombination.navigateTo({
        url: queriesString
          ? `${nativeRoute.mp}?${queriesString}`
          : nativeRoute.mp
      })
    }
  } else {
    NODE_ENV === 'development' &&
      console.warn(
        `跳转失败：页面位于小程序 Webview 中，但 ${
          !nativeRoute?.mp
            ? nativeRoute?.app +
              ' 或 ' +
              nativeRoute?.h5 +
              ' 没有对应的小程序路径'
            : '微信 jssdk 未初始化成功'
        }`
      )
  }
}

/**
 * 跳转到 App 原生页面
 * @param {nativeRoute} nativeRoute - 要跳转到的原生路由对象
 * @param {Object.<string, string>} queries - queries 参数
 */
const redirectToApp = (nativeRoute, queries, ssuIdFromH5) => {
  // [!TODO]此处是因为安卓端现在带参数过去会有问题，等他们处理完了就去掉下面的兼容逻辑
  // 兼容时间：20200606
  // 兼容版本：2.22(2.22以前的版本不能带参数)
  const noExtraParam =
    platform === 'android' && !checkAppVersion(ANDROID_SESSION_ID)
  const tracerExtraParams = noExtraParam ? {} : getTracerExtraParams()
  queries = Object.assign(tracerExtraParams, queries)

  if (nativeRoute?.app && Bridge.prepared) {
    if (nativeRoute.app === nativeRoutes.goodsDetail.app) {
      ssuIdFromH5 && (queries.id = ssuIdFromH5)
    }
    Bridge.navigateTo(nativeRoute.app, queries)
  } else {
    NODE_ENV === 'development' &&
      console.warn(
        `跳转失败：页面位于美菜商城 App 中，但 ${
          !nativeRoute?.app
            ? nativeRoute?.mp +
              ' 或 ' +
              nativeRoute?.h5 +
              ' 没有对应的美菜商城 App 路径'
            : '美菜商城 App JSBridge 未初始化成功'
        }`
      )
  }
}

/**
 * 跳转到 H5 原生页面
 * @param {nativeRoute} nativeRoute - 要跳转到的原生路由对象
 * @param {Object.<string, string>} queries - queries 参数
 */
// eslint-disable-next-line no-unused-vars
const redirectToH5 = (nativeRoute, queries, hashQueries, ssuIdFromH5) => {
  if (nativeRoute?.h5) {
    let [path, hash] = nativeRoute.h5.split('#')
    const queriesString = new Url('').stringifyQueryObj(queries)
    const hashQueriesString = new Url('').stringifyQueryObj(hashQueries)
    if (nativeRoute.h5 === nativeRoutes.goodsDetail.h5) {
      hash = `${hash}/ssu/${ssuIdFromH5 || hashQueries.id || queries.id}`
    }
    const url = `${path}?${queriesString}${
      hash
        ? '#' +
          hash +
          (hashQueriesString
            ? (hash.indexOf('?') > -1 ? '&' : '?') + hashQueriesString
            : '')
        : ''
    }`
    h5Redirect(url)
  } else if (nativeRoute?.app) {
    openApp({
      route: {
        path: nativeRoute.app,
        query: queries
      }
    })
  } else {
    NODE_ENV === 'development' &&
      console.warn(
        `跳转失败：页面位于微信 Webview 或浏览器中，但 ${nativeRoute?.mp} 没有对应的 h5 路径。`
      )
  }
}

/**
 * 跳转到原生页面
 * @param {nativeRoute} nativeRoute - 要跳转到的原生路由对象
 * @param {Object.<string, string>} queries - queries 参数
 */
export const redirect = ({
  nativeRoute,
  queries = {},
  h5HashQueries,
  ssuIdFromH5
}) => {
  if (miniProgramEnv.isMiniProgram) {
    redirectToMiniProgram(nativeRoute, queries, ssuIdFromH5)
  } else if (isMallApp()) {
    redirectToApp(nativeRoute, queries, ssuIdFromH5)
  } else {
    // 非app 小程序点击无反应
    // redirectToH5(nativeRoute, queries, h5HashQueries, ssuIdFromH5)
  }
}

/**
 * 跳转到原生页面（包括 美菜商城App, 美菜商城小程序和美菜商城 H5）。
 * 无论传入的是哪个环境的 url, 都会判断实际上的环境跳转到对应的页面。
 * eg: 传入的是 app 商品详情的 url, 但是页面运行在小程序中，实际上会跳转到小程序的商品详情页面。
 * @param {(string | nativeRoute)} target - 跳转目标
 * @param {Object.<string, string>} queries - queries 参数
 */
export const nativeRedirect = (target, queries) => {
  if (typeof target === 'string') {
    const nativeRoute = /^mall:\/\//.test(target)
      ? matchAppUrl(target)
      : /^https?:\/\//.test(target)
      ? matchH5Url(target)
      : matchMiniProgramUrl(target)
    if (nativeRoute) {
      const url = new Url(target)
      const urlQueries = url.parseQuerystring()
      const finalQueries = Object.assign({}, urlQueries, queries)
      const h5HashQueries =
        /^https?:\/\//.test(target) && url.hash
          ? url.Hash.parseQuerystring() || {}
          : {}
      let ssuIdFromH5 = ''
      if (nativeRoute.h5 === nativeRoutes.goodsDetail.h5) {
        const ssuStr = target.match(/\/ssu\/\d+/)
        ssuIdFromH5 = ssuStr && ssuStr[0] ? ssuStr[0].slice(5) : 0
      }
      redirect({
        nativeRoute,
        queries: finalQueries,
        h5HashQueries,
        ssuIdFromH5
      })
    } else {
      NODE_ENV === 'development' &&
        console.warn(
          '参数错误：nativeRedirect 未匹配到您传入的 APP URL.',
          '你传入的 target 的是:',
          target
        )
    }
  } else if (target.app || target.mp || target.h5) {
    redirect({
      nativeRoute: target,
      queries,
      h5HashQueries: queries
    })
  } else {
    NODE_ENV === 'development' &&
      console.warn(
        '参数错误：nativeRedirect 方法跳转原生页面 target 参数必须传入 app url 或者 nativeRoutes 中定义的 nativeRoute 对象。',
        '你传入的 target 的是:',
        target
      )
  }
}

/**
 * 根据一个 url 来判断这个页面是否有对应环境的原生页面
 * eg: 传入一个 app 的商品详情 url, 但页面运行在小程序中，判断的是小程序有没有原生的商品详情页面。
 * @param {string} url - 要判断的 url
 * @return {boolean} - 是否有对应的原生页面
 */
export const hasNativePage = url => {
  if (typeof url === 'string') {
    const nativeRoute = /^mall:\/\//.test(url)
      ? matchAppUrl(url)
      : /^https?:\/\//.test(url)
      ? matchH5Url(url)
      : matchMiniProgramUrl(url)
    if (miniProgramEnv.isMiniProgram) {
      return !!nativeRoute?.mp
    } else if (isMallApp()) {
      return !!nativeRoute?.app
    } else {
      return !!nativeRoute?.h5
    }
  } else {
    NODE_ENV === 'development' &&
      console.warn(
        '参数错误：hasNativePage 方法必须传入一个字符串类型的 url.',
        '你传入的 url 的是:',
        url
      )
  }
}

/**
 * @description 检测是否当前版本大于指定版本
 * @param {*} version 需要判断的版本号
 */
export const checkAppVersion = version => {
  const href = window.location.href
  if (href.indexOf('app_version') > -1) {
    const appVersion = new Url(href).parseQuerystring().app_version
    if (appVersion && appVersion.indexOf('.')) {
      const arr1 = appVersion.split('.').map(item => parseInt(item))
      const arr2 = version.split('.').map(item => parseInt(item))
      if (arr1[0] > arr2[0]) {
        return true
        // eslint-disable-next-line eqeqeq
      } else if (arr1[0] == arr2[0]) {
        if (arr1[1] > arr2[1]) {
          return true
          // eslint-disable-next-line eqeqeq
        } else if (arr1[1] == arr2[1]) {
          if (arr1[2] >= arr2[2]) {
            return true
          }
        }
      }
    }
  }
  return false
}

/**
 * @description 检测是否当前版本大于等于指定版本
 * @param {*} version 需要判断的版本号
 */
export const checkAppInfoAppVersion = version => {
  if (appVersion && appVersion.indexOf('.')) {
    const arr1 = appVersion.split('.').map(item => parseInt(item))
    const arr2 = version.split('.').map(item => parseInt(item))
    if (arr1[0] > arr2[0]) {
      return true
      // eslint-disable-next-line eqeqeq
    } else if (arr1[0] == arr2[0]) {
      if (arr1[1] > arr2[1]) {
        return true
        // eslint-disable-next-line eqeqeq
      } else if (arr1[1] == arr2[1]) {
        if (arr1[2] >= arr2[2]) {
          return true
        }
      }
    }
  }
  return false
}
